import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTimer } from 'react-timer-hook';

import { OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';
import { NULL_ADDRESS } from 'shared/constants/constants';
import { RoomStatusEnum } from 'shared/constants/shared-enums';
import { useRoomStore } from 'shared/store/roomStore';
import { epochToLocalUTC } from 'shared/utils/time';

import iconScoreCenterCircle from 'assets/png/bg-score-center.png?format=webp;avif&as=picture';
import iconScoreLeft from 'assets/png/bg-score-left.png?format=webp;avif&as=picture';
import iconScoreRight from 'assets/png/bg-score-right.png?format=webp;avif&as=picture';
import IconScoreCenter from 'assets/svg/bg-frame.svg?react';

export const Score: FC = () => {
  const { t } = useTranslation();
  const { amIPlayerA, playerA, playerB, roundCounter, closerRoom, roomStatus } = useRoomStore();

  // Pull the raw epoch (could be seconds or ms depending on backend)
  const rawDeadline = closerRoom?.deadline;

  /**
   * Make a safe, future Date for the timer.
   * Returns null if we don't have a valid future timestamp yet.
   */
  const expiryDate = useMemo<Date | null>(() => {
    if (rawDeadline == null || rawDeadline <= 0) return null;

    // epochToLocalUTC should yield a Date or something coercible to Date.
    const maybeDate = epochToLocalUTC(rawDeadline) as unknown as Date;
    const dateObj = maybeDate instanceof Date ? maybeDate : new Date(maybeDate as any);

    if (Number.isNaN(dateObj.getTime())) return null; // invalid date
    if (dateObj.getTime() <= Date.now()) return null; // already in the past
    return dateObj;
  }, [rawDeadline]);

  // Initialize timer paused with a harmless future timestamp.
  // We'll start it explicitly when expiryDate becomes valid.
  const { seconds, minutes, restart } = useTimer({
    expiryTimestamp: new Date(Date.now() + 1000),
    autoStart: false,
  });

  useEffect(() => {
    if (expiryDate) {
      // Restart timer only when we have a valid, future date
      restart(expiryDate, true);
    }
  }, [expiryDate, restart]);

  const opponentAddress = useMemo(
    () => (amIPlayerA ? playerB?.address : playerA?.address),
    [amIPlayerA, playerA, playerB],
  );
  const noOpponent = useMemo(() => opponentAddress === NULL_ADDRESS, [opponentAddress]);
  const preGame = useMemo(() => roundCounter === 0, [roundCounter]);
  const finished = useMemo(() => roomStatus === RoomStatusEnum.Closed, [roomStatus]);

  const showWaiting = useMemo(() => preGame && !noOpponent, [preGame, noOpponent]); // opponent invited, waiting to start
  const showSearching = useMemo(
    () => preGame && noOpponent && !finished,
    [preGame, noOpponent, finished],
  ); // room created, waiting for opponent
  const showRoundTimer = useMemo(
    () => !preGame && !finished && !!expiryDate,
    [preGame, finished, expiryDate],
  ); // only when the game started and we have a valid expiry

  const leftWins =
    useMemo(() => (amIPlayerA ? playerA?.wins : playerB?.wins), [amIPlayerA, playerA, playerB]) ??
    0;
  const rightWins =
    useMemo(() => (amIPlayerA ? playerB?.wins : playerA?.wins), [amIPlayerA, playerA, playerB]) ??
    0;

  return (
    <>
      <div className="mx-auto flex w-[9.625rem] items-center justify-between sm:w-[11.5rem] lg:w-full">
        <div className="relative flex items-center justify-center">
          <OptimizedImage
            imageData={iconScoreLeft}
            alt="rock paper scissors logo"
            className="w-[3.75rem] lg:h-[5.625rem] lg:w-[7.125rem]"
            imgClassName="object-fill"
          />
          <span
            className="absolute text-[2rem] leading-[2rem] lg:text-[5rem] lg:leading-[5rem]"
            style={{
              background: 'linear-gradient(180deg, #FFFFFF 0%, #FF8B00 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {leftWins?.toString()}
          </span>
        </div>

        <div className="score-center relative flex items-center justify-center">
          <IconScoreCenter
            className="absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 transform lg:block"
            height={124}
          />
          <OptimizedImage
            imageData={iconScoreCenterCircle}
            alt="rock paper scissors logo"
            className="h-[2.25rem] w-[2.25rem] lg:h-[5.625rem] lg:w-[5.625rem]"
          />
          <p className="absolute top-[50%] -translate-y-1/2 transform font-poppins text-base font-bold leading-4 text-white lg:text-[2.5rem]">
            {t('common.vs')}
          </p>
        </div>

        <div className="relative flex items-center justify-center">
          <OptimizedImage
            imageData={iconScoreRight}
            alt="rock paper scissors logo"
            className="w-[3.75rem] lg:h-[5.625rem] lg:w-[7.125rem]"
            imgClassName="object-fill"
          />
          <span
            className="absolute text-[2rem] leading-[2rem] lg:text-[5rem] lg:leading-[5rem]"
            style={{
              background: 'linear-gradient(180deg, #FFFFFF 0%, #4FB5FF 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {rightWins?.toString()}
          </span>
        </div>
      </div>

      <div className="mx-auto flex w-[9.625rem] items-center justify-center rounded-[0.375rem] border-[0.0625rem] border-border bg-[#0C093D] py-1 text-[0.625rem] font-[600] leading-[0.625rem] text-[#4E6CDE] sm:w-[11.5rem] sm:rounded-xl sm:py-4 sm:text-[1.25rem] lg:mx-0 lg:w-full lg:max-w-none lg:rounded-[1rem] lg:text-[1.5rem] lg:leading-[1.5rem]">
        {showRoundTimer && (
          <>
            <span className="border-0.5 mr-2 border-r border-[#272D63] pr-2 lg:mr-6 lg:pr-6">
              {t('common.round')} {roundCounter}
            </span>
            <span>
              {minutes.toString().padStart(2, '0')}:{seconds.toString().padStart(2, '0')}
            </span>
          </>
        )}
        {!showRoundTimer && showSearching && (
          <span className="capitalize">{t('common.searching')}</span>
        )}
        {!showRoundTimer && showWaiting && (
          <span className="capitalize">{t('common.waiting')}</span>
        )}
        {!showRoundTimer && finished && <span className="capitalize">{t('common.finished')}</span>}
      </div>
    </>
  );
};
